import React, { useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { fetchWarehouseActivityLog } from '../../api/warehouse';

const today = new Date().toISOString().slice(0, 10);
const weekAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

const WarehouseActivityLog = ({ theme = 'light' }) => {
    const [startDate, setStartDate] = useState(weekAgo);
    const [endDate, setEndDate] = useState(today);
    const [entries, setEntries] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const isDark = theme === 'dark';

    const colors = {
        background: isDark ? '#212529' : '#fff',
        text: isDark ? '#f8f9fa' : '#212529',
        muted: isDark ? '#adb5bd' : '#6c757d',
        border: isDark ? '#495057' : '#dee2e6',
        input: isDark ? '#2b3035' : '#fff',
        tableHeader: isDark ? '#343a40' : '#f8f9fa',
    };

    const search = async (event) => {
        event.preventDefault();
        setError('');
        if (!startDate || !endDate) {
            setError('Choose both a start and end date.');
            return;
        }
        if (startDate > endDate) {
            setError('The start date cannot be after the end date.');
            return;
        }

        setIsLoading(true);
        const response = await fetchWarehouseActivityLog(startDate, endDate);
        if (response.status === 200) {
            setEntries(response.data.entries || []);
            setHasSearched(true);
        } else {
            setError(response.data?.error || 'Unable to load warehouse activity.');
        }
        setIsLoading(false);
    };

    const formatDateTime = (value) => {
        if (!value) return '—';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime())
            ? value
            : parsed.toLocaleString('en-GB', { hour12: false });
    };

    return (
        <section
            className={`warehouse-activity-log ${isDark ? 'dark' : 'light'}`}
            style={{ color: colors.text }}
        >
            <header className="warehouse-activity-log__header">
                <div>
                    <h2>Warehouse Activity Log</h2>
                    <p style={{ color: colors.muted }}>
                        Review products moved into, out of, or between warehouse locations.
                    </p>
                </div>
            </header>

            <Form className="warehouse-activity-log__filters" onSubmit={search}>
                <Form.Group>
                    <Form.Label>From</Form.Label>
                    <Form.Control
                        type="date"
                        value={startDate}
                        max={endDate || today}
                        onChange={(event) => setStartDate(event.target.value)}
                        disabled={isLoading}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>To</Form.Label>
                    <Form.Control
                        type="date"
                        value={endDate}
                        min={startDate}
                        max={today}
                        onChange={(event) => setEndDate(event.target.value)}
                        disabled={isLoading}
                    />
                </Form.Group>
                <Button type="submit" disabled={isLoading}>
                    {isLoading ? <><Spinner size="sm" className="me-2" />Loading...</> : 'View activity'}
                </Button>
            </Form>

            {error && <Alert variant="danger">{error}</Alert>}

            {hasSearched && !isLoading && !error && (
                <>
                    <p className="warehouse-activity-log__summary" style={{ color: colors.muted }}>
                        {entries.length} {entries.length === 1 ? 'entry' : 'entries'} from {startDate} to {endDate}
                    </p>
                    {entries.length === 0 ? (
                        <Alert variant="info">No warehouse activity found for this date range.</Alert>
                    ) : (
                        <div className="warehouse-activity-log__table-wrap">
                            <Table bordered hover responsive variant={isDark ? 'dark' : undefined}>
                                <thead style={{ background: colors.tableHeader }}>
                                    <tr>
                                        <th>Date / time</th>
                                        <th>Customer</th>
                                        <th>Product</th>
                                        <th>Quantity</th>
                                        <th>Action</th>
                                        <th>Location</th>
                                        <th>Recorded by</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry) => {
                                        const profile = entry.profiles || {};
                                        const performedBy = [profile.name, profile.surname].filter(Boolean).join(' ');
                                        return (
                                            <tr key={entry.id}>
                                                <td>{formatDateTime(entry.timestamp)}</td>
                                                <td>{entry.customers?.company || `Customer ${entry.customer_id}`}</td>
                                                <td>{entry.products?.product_name || '—'}</td>
                                                <td>{entry.quantity}</td>
                                                <td>{entry.action || '—'}</td>
                                                <td>
                                                    {entry.pallets?.pallet_code || '—'}
                                                    {entry.pallets?.lane_id && (
                                                        <small className="warehouse-activity-log__lane">
                                                            {entry.pallets.lane_id}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{performedBy || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}
            <style>{`
                .warehouse-activity-log { padding: 8px; }
                .warehouse-activity-log__header { margin-bottom: 20px; }
                .warehouse-activity-log__header h2 { margin: 0 0 6px; }
                .warehouse-activity-log__header p { margin: 0; }
                .warehouse-activity-log__filters {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(150px, 220px)) auto;
                    align-items: end;
                    gap: 12px;
                    margin-bottom: 18px;
                }
                .warehouse-activity-log__filters .form-label { color: ${colors.text}; }
                .warehouse-activity-log__filters .form-control {
                    color: ${colors.text};
                    background: ${colors.input};
                    border-color: ${colors.border};
                    color-scheme: ${isDark ? 'dark' : 'light'};
                }
                .warehouse-activity-log__filters button {
                    justify-self: start;
                    width: auto;
                    min-width: 135px;
                }
                .warehouse-activity-log__summary { margin: 0 0 10px; }
                .warehouse-activity-log__table-wrap { overflow-x: auto; }
                .warehouse-activity-log table { color: ${colors.text}; }
                .warehouse-activity-log thead { color: ${colors.text}; }
                .warehouse-activity-log__lane {
                    display: block;
                    color: ${colors.muted};
                    font-size: 0.8em;
                }
                @media (max-width: 700px) {
                    .warehouse-activity-log__filters { grid-template-columns: 1fr 1fr; }
                }
            `}</style>
        </section>
    );
};

export default WarehouseActivityLog;
